import React, { useEffect, useState } from "react";
import {
  fetchChatPage,
  fetchAddChat,
  fetchUpdateChat,
} from "store/slices/chat";
import { makeStyles } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import { Box, TextareaAutosize, Typography } from "@material-ui/core";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import { useDispatch, useSelector } from "react-redux";
import { getAuthInfo } from "store/slices/auth";
import SendIcon from "@material-ui/icons/Send";
import PropTypes from "prop-types";
import moment from "moment";
import Dropdown from "react-bootstrap/Dropdown";
import ComponentLoading from "../../../../components/Common/ComponentLoading";
import AppButton from "components/CustomButtons/AppButton";
import { Formik } from "formik";
import * as Yup from "yup";
import AppConfig from "config/AppConfig";
import ReadMore from "components/Input/ReadMoreText";
import IconButton from "@material-ui/core/IconButton";
import CachedIcon from "@material-ui/icons/Cached";
import AppColor from "utilities/AppColor";
const { PATTERNS } = AppConfig;

const useStyles = makeStyles(() => ({
  noteContent: {
    boxSizing: "border-box",
    border: "1px solid rgba(0, 0, 0, 0.1)",
    boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
    backgroundColor: "#FFFFFF",
    paddingBottom: "20px",
  },
  chatBox: {
    width: "100%",
    height: "auto",
    border: "1px #000000 10%",
    padding: "20px",
  },
  icons: {
    cursor: "pointer",
    height: "49px",
    width: "49px",
    background: "#FFFFFF",
    border: "0.5px solid #000000",
    borderRadius: "24px",
  },
  listNote: {
    width: "100%",
    height: "auto",
    border: "1px #000000 10%",
    padding: "20px",
  },
  horizontalBar: {
    marginTop: "1rem",
    marginBottom: "1rem",
    border: "1px solid #cacaca",
    textAlign: "center",
    width: "95%",
    borderTop: "0",
  },
  customUserName: {
    fontStyle: "normal",
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "17px",
    // marginLeft: "10px",
  },
  customDayNote: {
    fontStyle: "normal",
    fontWeight: 400,
    fontSize: "12px",
    lineHeight: "15px",
    color: "#6B7280",
    marginLeft: "5px",
  },
  textField: {
    width: "100%",
    "& .MuiInputBase-input": {
      height: "200px",
      verticalAlign: "top",
    },
  },
  readMore: {
    backgroundColor: "#FFFFFF",
    color: "#000000",
    border: "0px",
    fontSize: "14px",
  },
  dropDown: {
    "& .dropdown-toggle::after": {
      display: "none",
    },
    "& .dropdown-toggle": {
      backgroundColor: "transparent !important",
      color: "black",
      border: "none",
    },
    "& .dropdown-toggle:focus": {
      boxShadow: "none !important",
    },
    "& .dropdown-menu": {
      minWidth: "auto",
    },
    "& .dropdown-item": {
      padding: "0.25rem 0.5rem",
      fontSize: "14px",
      textAlign: "right",
    },
  },
  pagination: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "20px",
    marginRight: "20px",
  },
  shiftInfo: {
    padding: "4px 8px",
    borderRadius: "4px",
    display: "inline-block",
  },
  loadMoreText: {
    fontSize: "13px",
    fontWeight: 500,
    color: "#000000",
    cursor: "pointer",
    transition: "opacity 0.2s ease-in-out",
    userSelect: "none",
    "&:hover": {
      opacity: 0.5,
    },
  }
}));

const ChatContents = (props) => {
  const { jobId, workerId, resetWorker, isDisable, bookingId, title, shiftType, fetchDataSuccess = () => {} } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const authInfo = useSelector(getAuthInfo) || {};
  const [inputText, setInputText] = useState("");
  const [sendingInline, setSendingInline] = useState(false);

  const [localChatData, setLocalChatData] = useState([]);
  const [localPagination, setLocalPagination] = useState({ page: 1, pageSize: 10, totalPages: 0, totalElements: 0 });
  const [localIsLoading, setLocalIsLoading] = useState(false);
  const [isAppending, setIsAppending] = useState(false);

  const [isOpenEdit, setOpenEdit] = useState(false);
  const [isReadMore, setReadMore] = useState(false);
  const [idNote, setIdNote] = useState(0);
  const [message, setMessage] = useState("");
  const { t } = useTranslation();

  const messagesEndRef = React.useRef(null);
  const messagesContainerRef = React.useRef(null);

  const fetchLocalChats = async (page = 1, append = false) => {
    if (append) {
      setIsAppending(true);
    } else {
      setLocalIsLoading(true);
    }
    const params = {
      jobId: jobId,
      pageNumber: page,
      pageSize: localPagination.pageSize || 10,
      bookingId: bookingId
    };
    try {
      const result = await dispatch(fetchChatPage(params));
      if (fetchChatPage.fulfilled.match(result)) {
        const payload = result.payload;
        if (payload) {
          fetchDataSuccess(!!payload.content?.length);
          if (append) {
            setLocalChatData(prev => {
              const seenIds = new Set(prev.map(item => item.id));
              const newItems = (payload.content || []).filter(item => !seenIds.has(item.id));
              return [...prev, ...newItems];
            });
          } else {
            setLocalChatData(payload.content || []);
          }
          setLocalPagination({
            ...localPagination,
            page: payload.pageNumber,
            totalPages: payload.totalPages,
            totalElements: payload.totalElements
          });
        }
      }
    } finally {
      setLocalIsLoading(false);
      setIsAppending(false);
    }
  };

  // Scroll to load more when reaching bottom of messages container
  const handleMessagesScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 50 && !isAppending && localPagination.page < localPagination.totalPages) {
      fetchLocalChats(localPagination.page + 1, true);
    }
  };

  const handleSendInlineMessage = async () => {
    if (!inputText.trim()) return;
    setSendingInline(true);
    const request = {
      jobId,
      bookingId,
      note: inputText.trim()
    };
    const result = await dispatch(fetchAddChat(request));
    setSendingInline(false);
    if (fetchAddChat.fulfilled.match(result)) {
      resetWorker && resetWorker();
      setInputText("");
      fetchLocalChats(1);
    }
  };

  useEffect(() => {
    if (jobId) {
      fetchLocalChats(1);
    }
  }, [jobId, bookingId]);

  const handleChangeIncident = async (data) => {
    const request = {
      id: data.id,
      note: data.note,
      isIncident: !data.isIncident,
      queries: {
        pageNumber: 1,
        pageSize: localPagination.pageSize,
        jobId: jobId,
        workerId: workerId
      },
    };
    const result = await dispatch(fetchUpdateChat(request));
    console.log(' >>> result: ', result);
    
    if (fetchUpdateChat.fulfilled.match(result) && !!result?.payload?.id) {
      // Update only the specific message in local state instead of reloading all
      setLocalChatData(prev =>
        prev.map(item =>
          item.id === data.id ? { ...item, isIncident: !item.isIncident } : item
        )
      );
    }
  };

  const handleChangeNote = async (event, item) => {
    event.preventDefault();
    const request = {
      note: message,
      id: item.id,
      isIncident: item.isIncident,
      queries: {
        pageNumber: 1,
        pageSize: localPagination.pageSize,
        jobId: jobId,
        workerId: workerId
      },
    };
    const result = await dispatch(fetchUpdateChat(request));
    if (fetchUpdateChat.fulfilled.match(result)) {
      fetchLocalChats(1, false);
    }
    setOpenEdit(false);
  };

  const handleLoadMore = (event) => {
    event.preventDefault();
    if (localPagination.page < localPagination.totalPages) {
      fetchLocalChats(localPagination.page + 1, true);
    }
  };

  const handleReloadPage = (event) => {
    event.preventDefault();
    fetchLocalChats(1, false);
  };

  const isCharacterDownTheLine = (text) => {
    return text.indexOf("\n") != -1 ? true : false;
  };
  const isTooManyCharacterDownTheLine = (text) => {
    return text.split("\n").length > 3 ? true : false;
  };

  const formatMessageReadMore = (item) => {
    const newMessage = !isTooManyCharacterDownTheLine(item.note) ?
      (item.note.split("\n").map((str, index) => <div key={str + item.id + index}>{str + " "}</div>))
      : item.note.split("\n").map((str, index) =>
          index === 0 ? (<div key={str + 1 + index + item.id + 2}>{str}</div>) 
          : index === 1 ? (<div key={str + 1 + index + item.id + 2}>{`${str}...`}
              <span><a href="#" style={{ width: "90px", fontWeight: 600 }} onClick={(event) => { event.preventDefault(); setReadMore(true); setIdNote(item.id) }}>{t("button.readMore")}</a></span>
            </div>) 
          : ("")
        );
    return <div key={`${item.id}-read-more`} id={`${item.id}-read-more`}>{newMessage}</div>;
  };

  const formatMessageShowLess = (item) => {
    const newMessage = item.note.split("\n").map((str, index) =>
        index < item.note.split("\n").length - 1 ? (
          <div key={str + 1 + index + item.id}>{str}</div>
        ) : index === item.note.split("\n").length - 1 ? (
          <div key={str + 1 + index + item.id}>{str}
            <span><a href="#" style={{ width: "90px", fontWeight: 600 }} onClick={(event) => { event.preventDefault(); setReadMore(false); setIdNote(item.id) }}>{t("button.showLess")}</a></span>
          </div>
        ) : ("")
      );
    return <div key={`${item.id}-show-less`} id={`${item.id}-show-less`}>{newMessage}</div>;
  };

  const renderMoreAction = (item) => (
    <Dropdown className={classes.dropDown}>
      <Dropdown.Toggle>
        <MoreHorizIcon></MoreHorizIcon>
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {isDisable ? (
          <Dropdown.Item disabled>{t("label.reportIncident")}</Dropdown.Item>
        ) : (
          <Dropdown.Item
            onClick={() => {
              handleChangeIncident(item);
            }}
          >
            {item.isIncident ? t("label.cancelIncident") : t("label.reportIncident")}
          </Dropdown.Item>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );

  const renderTextNote = (item) => (
    <React.Fragment key={`${item.id}-${item.clientId ? item.clientId : item.workerId ? item.workerId : " "}`}>
      {item.note.length < 67 ? (
        isCharacterDownTheLine(item.note) ? (
          isReadMore && idNote === item.id ? formatMessageShowLess(item) : formatMessageReadMore(item)
        ) : (item.note)
      ) : (
        <ReadMore lines={2}>{item.note}</ReadMore>
      )}
    </React.Fragment>
  );

  return (
    <Box style={{ height: '100%' }} id={`chat-contents-${bookingId || 'job'}`}>
      <Box className={classes.noteContent} style={{ display: "flex", flexDirection: "column", overflow: "hidden", height: '100%' }}>
        
        {/* Header bar - FIXED at top */}
        <Box style={{ flexShrink: 0, borderBottom: "1px solid #E5E7EB", padding: "12px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box style={{ fontSize: 14, fontWeight: "bold", color: "#374151" }}>Messages</Box>
          <IconButton color="primary" aria-label="reload page" onClick={handleReloadPage} disabled={localIsLoading || isDisable} style={{ padding: 8 }}>
            <CachedIcon />
          </IconButton>
        </Box>

        {localIsLoading && (
          <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
            <ComponentLoading />
          </Box>
        )}

        {/* Message Feed Area - SCROLLABLE */}
        <Box
          ref={messagesContainerRef}
          onScroll={handleMessagesScroll}
          style={{ backgroundColor: "#F9FAFB", flex: 1, overflowY: "auto", minHeight: 0 }}
        >
          {!localIsLoading && localChatData && localChatData.length > 0 ? (
            <Box>
              {localChatData.map((item, index) => {
                const isMe = (item.companyUserId && item.companyUserId === authInfo.id) || (item.workerId && item.workerId === authInfo.id);
                const initials = ((item.firstName ? item.firstName[0] : '') + (item.lastName ? item.lastName[0] : '')).toUpperCase() || 'U';
                const senderName = isMe ? 'You' : (item.firstName + " " + item.lastName);
                const timeStr = moment(new Date(item.createdDate)).format("DD/MM/YYYY HH:mm");

                return (
                  <Box 
                    key={`note-${index}-${item.id}-${item.clientId || item.workerId}`}
                    display="flex"
                    flexDirection="row"
                    justifyContent={isMe ? "flex-end" : "flex-start"}
                    alignItems="flex-end"
                    p={2}
                    style={{ 
                      backgroundColor: item.isIncident ? "#FEE2E2" : "transparent"
                    }}
                  >
                    {!isMe && (
                      <Box 
                        display="flex" 
                        alignItems="center" 
                        justifyContent="center"
                        style={{ 
                          width: "36px", 
                          height: "36px", 
                          borderRadius: "18px", 
                          backgroundColor: "#E9D5FF", 
                          marginRight: "10px",
                          color: "#7C3AED",
                          fontWeight: "bold",
                          fontSize: "13px",
                          flexShrink: 0
                        }}
                      >
                        {item.avatarAws3Link ? (
                          <img src={item.avatarAws3Link} alt="avatar" style={{ width: "36px", height: "36px", borderRadius: "18px", objectFit: "cover" }} />
                        ) : (
                          initials
                        )}
                      </Box>
                    )}
                    
                    <Box display="flex" flexDirection="column" style={{ maxWidth: "70%" }}>
                      <Typography 
                        variant="caption" 
                        style={{ 
                          color: "#9CA3AF", 
                          marginBottom: "4px",
                          textAlign: isMe ? "right" : "left",
                          paddingRight: isMe ? "4px" : "0",
                          paddingLeft: !isMe ? "4px" : "0"
                        }}
                      >
                        {`${senderName} · ${timeStr}`}
                      </Typography>
                      
                      <Box display="flex" flexDirection="row" alignItems="center">
                        {isMe && (
                          <Box style={{ marginRight: "4px" }}>
                            {renderMoreAction(item)}
                          </Box>
                        )}
                        
                        <Box 
                          style={{ 
                            backgroundColor: isMe ? AppColor.mainColor : "#E5E7EB", 
                            color: isMe ? "#FFFFFF" : "#1F2937",
                            padding: "10px 14px",
                            borderRadius: "12px",
                            borderBottomLeftRadius: !isMe ? "0" : "12px",
                            borderBottomRightRadius: isMe ? "0" : "12px",
                            border: !isMe ? "1px solid #E5E7EB" : "none",
                            fontSize: "14px",
                            lineHeight: "1.4",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word"
                          }}
                        >
                          {isOpenEdit && item.id === idNote ? (
                            <Formik
                              enableReinitialize={true}
                              initialValues={{ editNote: "" }}
                              validationSchema={Yup.object().shape({
                                note: Yup.string().trim().matches(PATTERNS.BLANK_SPACES, "validation.blankSpaces").max(1000, "validation.max_length_1000"),
                              })}
                            >
                              {(formikProps) => {
                                const { setFieldValue } = formikProps;
                                return (
                                  <Box>
                                    <TextareaAutosize
                                      name="editNote"
                                      style={{ width: "100%", height: "80px", color: "#000000", padding: "10px" }}
                                      onChange={(event) => {
                                        setFieldValue("editNote", event.target.value);
                                        setMessage(event.target.value);
                                      }}
                                      value={message}
                                    />
                                    <Box display="flex" justifyContent="flex-end" mt={1} gap={1}>
                                      <AppButton label={`${t("button.save_changes")}`} type="button" onClick={(event) => handleChangeNote(event, item)} style={{ fontSize: "12px", padding: "4px 8px" }} />
                                      <AppButton className="ml-1" label={`${t("button.cancel")}`} type="button" mode="outlined" onClick={() => setOpenEdit(false)} style={{ fontSize: "12px", padding: "4px 8px" }} />
                                    </Box>
                                  </Box>
                                );
                              }}
                            </Formik>
                          ) : (
                            renderTextNote(item)
                          )}
                        </Box>
                        
                        {!isMe && (
                          <Box style={{ marginLeft: "4px" }}>
                            {renderMoreAction(item)}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : (
            !localIsLoading && (
              <Box display="flex" justifyContent="center" alignItems="center" style={{ minHeight: "100px", padding: "20px 0" }}>
                <Typography style={{ textAlign: "center", color: "#6B7280" }}>
                  {t("label.no_data")}
                </Typography>
              </Box>
            )
          )}
        </Box>

        {/* Inline Message Input - FIXED at bottom */}
        <Box display="flex" alignItems="center" p={2} style={{ borderTop: "1px solid #E5E7EB", backgroundColor: "#FFFFFF", flexShrink: 0 }}>
          <TextareaAutosize
            minRows={1}
            maxRows={4}
            placeholder={t("placeholder.message", "Message...")}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendInlineMessage();
              }
            }}
            disabled={isDisable}
            style={{ 
              flex: 1, 
              padding: "10px 12px", 
              borderRadius: "8px", 
              borderColor: "#D1D5DB", 
              resize: "none", 
              fontSize: "14px",
              fontFamily: "inherit",
              marginRight: "10px",
              boxSizing: "border-box"
            }}
          />
          <IconButton 
            onClick={handleSendInlineMessage} 
            disabled={!inputText.trim() || sendingInline || isDisable} 
            style={{ 
              backgroundColor: inputText.trim() && !sendingInline && !isDisable ? AppColor.mainColor : "#E5E7EB", 
              color: inputText.trim() && !sendingInline && !isDisable ? "#FFFFFF" : "#9CA3AF",
              padding: "10px",
              borderRadius: "50%"
            }}
          >
            <SendIcon style={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};
ChatContents.propTypes = {
  jobId: PropTypes.any,
  jobStatus: PropTypes.string,
  title: PropTypes.string,
  shiftType: PropTypes.string,
  workerId: PropTypes.any,
  resetWorker: PropTypes.func,
  isDisable: PropTypes.bool,
  bookingId: PropTypes.any,
  fetchDataSuccess: PropTypes.func,
};

export default ChatContents;
